import React, { ChangeEvent, ReactElement, useEffect, useState } from "react";
import { CategoryCreateViewModel, CategoryDetailViewModel, uploadApis } from "@payping/api";
import { Add, CuToman, Delete, InfoCircleOutlined } from "@payping/icons";
import { createCategory, getCategories, removeCategory } from "@payping/redux/actions";
import {
  Button,
  Checkbox,
  Col,
  Divider,
  Flex,
  Form,
  Input,
  Message,
  Modal,
  Row,
  Select,
  Space,
  TextArea,
  Tooltip,
  Typography,
  Upload,
  useMessagedAsyncFn,
} from "@payping/web-components";
import { UploadChangeParam, UploadFile } from "@payping/web-components/components/Upload/types";
import { useAsync } from "react-use";
import { useAppDispatch, useAppSelector } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { TypographyText } from "@payping/web-components/components/Typography/web";
import styles from "@/assets/scss/Shop/Product/form.module.scss";
import { ProductFormProps } from "./types";
import { SwitchRow } from "@/core/component";

const ProductForm: React.FC<ProductFormProps> = (props: ProductFormProps) => {
  const { isDirectPaymentActive, formRef, loading, details, setProductImageLink, setProductImageUrl } = props;

  const dispatch = useAppDispatch();
  const { t } = useAppTranslation();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [newTag, setNewTag] = useState<string>("");
  const [modal, contextHolder] = Modal.useModal();
  const [isAddTagModalOpen, setIsAddTagModalOpen] = useState<boolean>(false);

  const categories = useAppSelector((state) => state.categories.list);
  const { loading: loadingCategories } = useAsync(() => dispatch(getCategories("")), []);

  const [{ loading: loadingCreateTag }, createCategoryFn] = useMessagedAsyncFn(
    async (data: CategoryCreateViewModel): Promise<string> => dispatch(createCategory(data)),
    [],
    undefined,
    { success: false, useProgress: true },
  );

  useEffect(() => {
    if (details) {
      const { category, ...data } = details;
      formRef.setFieldsValue({
        ...data,
        categoryCode: category?.code,
      });
      if (details.imageLink) {
        setFileList([
          {
            name: "image",
            size: 0,
            type: "image/*",
            uid: "0",
            url: details.imageLink,
          },
        ]);
        setProductImageUrl(details.imageLink);
      }
    }
  }, [details, formRef, setProductImageUrl]);

  const uploadButton = (
    <div>
      <Add width={"14px"} className={"color--primary"} />
      <div className={"ant-upload-text"}>{t("product.form.upload.photo")}</div>
    </div>
  );

  const [, addTag] = useMessagedAsyncFn(
    async (): Promise<void> => {
      if (newTag) {
        const createdCategoryCode = await createCategoryFn({ name: newTag, products: [] });
        formRef.setFieldsValue({
          ...formRef.getFieldsValue(),
          categoryCode: createdCategoryCode,
        });
        setNewTag("");
        Message.success({
          content: t("messages.success", { operation: t("common.create"), subject: t("category") }),
        });
      }
    },
    [newTag, formRef],
    undefined,
    { useProgress: true },
  );

  async function deleteTag(
    event: React.MouseEvent<SVGSVGElement, MouseEvent>,
    category: CategoryDetailViewModel,
  ): Promise<void> {
    event.stopPropagation();
    modal.confirm({
      cancelText: t("cancel"),
      content: t("messages.removeCategory", { subject: category.name }),
      maskClosable: true,
      okText: t("ok"),
      onOk: async () => {
        if (category.code) {
          await dispatch(removeCategory(category.code));
          formRef.resetFields(["categoryCode"]);
        }
      },
    });
  }

  return (
    <Space direction={"vertical"} className={"tw-w-full"}>
      {/* < تصویر محصول > */}
      <Form.Item name={"imageLink"} label={t("product.form.upload.label")}>
        <Upload
          disabled={loading}
          listType={"picture-card"}
          requestFunction={uploadApis.itemWithFilePost}
          onChange={(info: UploadChangeParam): void => {
            setProductImageLink(info?.file?.originFileObj);
            setFileList(info.fileList);
          }}
          fileList={fileList}
          showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
          allowedExtensions={["mp4", "jpg", "jpeg", "png"]}
          showTooltip
        >
          {fileList.length ? null : uploadButton}
        </Upload>
      </Form.Item>
      {/* < تصویر محصول /> */}

      {/* < نام محصول > */}
      <Form.Item label={t("product.name")} rules={[{ required: true }]} name={"title"}>
        <Input skeleton={loading} />
      </Form.Item>
      {/* < نام محصول /> */}

      {/* < توضیحات محصول > */}
      <Form.Item label={t("description")} name={"description"}>
        <TextArea skeleton={loading} />
      </Form.Item>
      {/* < توضیحات محصول /> */}

      {/* <> */}
      <Form.Item
        label={t("product.form.price.title")}
        shouldUpdate={(prev, curr): boolean =>
          prev.defineAmountByUser !== curr.defineAmountByUser || prev.amount !== curr.amount
        }
      >
        {(form1): ReactElement => {
          const switchPriceByUser = form1.getFieldValue("defineAmountByUser");
          return (
            <Space size={"large"} direction={"vertical"} className="tw-w-full">
              {/*  < قیمت محصول > */}
              <Form.Item
                className={"!tw-mb-0"}
                rules={[
                  { required: !switchPriceByUser },
                  {
                    validator: (_, value): Promise<void> =>
                      value && !(parseInt(value, 10) >= 1000)
                        ? Promise.reject(t("error.minimum", { quantity: 1000 }))
                        : Promise.resolve(),
                  },
                ]}
                name={"amount"}
              >
                <Input.Number
                  disabled={switchPriceByUser}
                  skeleton={loading}
                  className="tw-w-full"
                  suffix={<CuToman width={24} height={24} />}
                  separateThousandth
                  inputMode="numeric"
                  changeOnWheel={false}
                  controls={false}
                  placeholder={t("product.form.price.placeHolder")}
                />
              </Form.Item>
              {/*  < قیمت محصول /> */}

              {/* < مالیات ارزش بر افزوده > */}
              <Form.Item noStyle name={"haveTax"} valuePropName={"checked"}>
                <Checkbox>{t("product.form.tax.label")}</Checkbox>
              </Form.Item>
              {/* < مالیات ارزش بر افزوده /> */}

              {/* < سویچ تعیین قیمت توسط مشتری > */}
              <Form.Item
                noStyle
                // this Form.Item is used inside LinkForm to enable or disable isMultiple value
                name={"defineAmountByUser"}
                valuePropName={"checked"}
              >
                <SwitchRow label={<TypographyText>{t("product.form.price.byUser")}</TypographyText>} />
              </Form.Item>
              {/* < سویچ تعیین قیمت توسط مشتری /> */}
            </Space>
          );
        }}
      </Form.Item>
      {/* </ > */}

      <Form.Item
        shouldUpdate={(prevValues, currentValues): boolean => prevValues.unlimited !== currentValues.unlimited}
      >
        {(form1): ReactElement => (
          <Flex vertical gap={4}>
            {t("quantity.title")}
            <Space.Compact className="tw-w-full">
              <Form.Item noStyle name={"unlimited"} rules={[{ required: true }]}>
                <Select skeleton={loading} className={styles.groupSelect}>
                  <Select.Option value={false}>{t("product.form.limited")}</Select.Option>
                  <Select.Option value={true}>{t("product.form.unlimited")}</Select.Option>
                </Select>
              </Form.Item>
              {!form1.getFieldValue("unlimited") && (
                <Form.Item
                  noStyle
                  rules={[
                    { required: true },
                    {
                      validator: (_, value): Promise<void> =>
                        value && !(parseInt(value, 10) >= 1)
                          ? Promise.reject(t("error.minimum", { quantity: 1 }))
                          : Promise.resolve(),
                    },
                  ]}
                  name={"quantity"}
                >
                  <Input.Number
                    skeleton={loading}
                    className="tw-w-full"
                    suffix={<CuToman width={24} height={24} />}
                    inputMode="numeric"
                    changeOnWheel={false}
                    controls={false}
                  />
                </Form.Item>
              )}
            </Space.Compact>
          </Flex>
        )}
      </Form.Item>

      {/* < سویچ تعیین قیمت توسط مشتری > */}
      <Form.Item name={"isMultiple"} valuePropName={"checked"}>
        {isDirectPaymentActive ? (
          <Checkbox disabled={formRef.getFieldValue("defineAmountByUser") || loading || !isDirectPaymentActive}>
            {t("product.form.perma.multiple")}
          </Checkbox>
        ) : (
          <Tooltip title={t("product.form.isMultipleDisabled")}>
            <Space>
              <InfoCircleOutlined width={14} className={"color--gray700"} />
              <Typography.Text className={"color--gray700"}>{t("product.form.perma.multiple")}</Typography.Text>
            </Space>
          </Tooltip>
        )}
      </Form.Item>

      {/* < سویچ تعیین قیمت توسط مشتری /> */}

      {/* < سکشن دسته بندی محصولات > */}
      <Form.Item label={t("product.form.tag.title")}>
        <Row gutter={[8, 8]}>
          <Col xs={24}>
            {/* < انتخاب یا ایجاد تگ جدید > */}
            <Form.Item noStyle name={"categoryCode"}>
              <Select
                placeholder={t("product.form.tag.select")}
                showSearch
                filterOption={(input, option): boolean => (option?.title as unknown as string).indexOf(input) >= 0}
                dropdownRender={(menu) => (
                  <>
                    <div className={"tw-w-full !tw-py-0"}>
                      <Button
                        type={"link"}
                        icon={<Add width={14} height={14} />}
                        className={"tw-w-full"}
                        onClick={() => setIsAddTagModalOpen(true)}
                      >
                        {t("product.form.tag.add")}
                      </Button>
                    </div>

                    <Divider className={"!tw-m-0"} />
                    {menu}
                  </>
                )}
                skeleton={loadingCategories || loading}
                allowClear
                optionLabelProp={"title"}
                options={categories.map((category, index) => ({
                  key: index,
                  label: (
                    <Space className={styles.categoryOption}>
                      <Tooltip title={t("delete")}>
                        <Delete
                          className={styles.categoryDelete}
                          onClick={(event: React.MouseEvent<SVGSVGElement, MouseEvent>) => deleteTag(event, category)}
                        />
                      </Tooltip>
                      {category.name}
                    </Space>
                  ),
                  title: category.name,
                  value: category.code || "",
                }))}
              />
            </Form.Item>
            {/* < انتخاب یا ایجاد تگ جدید /> */}
          </Col>

          {/* < مودال ایجاد تگ جدید > */}
          <Modal
            title={t("product.form.tag.add")}
            open={isAddTagModalOpen}
            onCancel={() => setIsAddTagModalOpen(false)}
            okButtonProps={{ loading: loading || loadingCreateTag }}
            onOk={async () => {
              await addTag();
              setIsAddTagModalOpen(false);
            }}
          >
            <Input
              skeleton={loading || loadingCreateTag}
              value={newTag}
              placeholder={t("product.form.tag.add")}
              onChange={(event: ChangeEvent<HTMLInputElement>): void => {
                setNewTag(event.target.value);
              }}
              autoFocus
            />
          </Modal>
          {/* < مودال ایجاد تگ جدید /> */}
        </Row>
      </Form.Item>

      {/* < سکشن دسته بندی محصولات /> */}

      {contextHolder}
    </Space>
  );
};

export default ProductForm;
